import React, { useState } from "react";

import {
	Box,
	Button,
	Container,
	Dialog,
	DialogActions,
	DialogContent,
	DialogContentText,
	DialogTitle,
	TextField,
	Typography
} from "@material-ui/core";
import { CardCvcElement, CardExpiryElement, CardNumberElement, useElements, useStripe } from "@stripe/react-stripe-js";

import { StripeElementChangeEvent, StripeElementType } from "@stripe/stripe-js";
import { useTranslation } from "react-i18next";

import { useNavigate } from "react-router";

import CustomerInfo from "components/CustomerInfo";
import { PromoCode } from "components/PromoCode";
import StripeTextField from "components/Stripe";
import { Terms } from "components/Terms";
import { Tip } from "components/Tip";
import makeStyles from "hooks/makeStyles";
import useCart from "hooks/useCart";
import ShoppingBagIcon from "icons/ShoppingBagIcon";
import SignOutAltIcon from "icons/SignOutAltIcon";
import { formatMoney } from "utility/formatMoney";

import OrderTotal from "./OrderTotal";

const useStyles = makeStyles(theme => ({
	root: {
		padding: 0,
		display: "flex",
		flex: 1,
		flexDirection: "column",
		height: "calc(var(--vh, 1vh) * 100 - 95px)", // not great..
		position: "relative",
		[theme.breakpoints.up(480)]: {
			overflow: "hidden",
			height: "100vh"
		}
	},
	body: {
		[theme.breakpoints.up(480)]: {
			backgroundColor: theme.palette.background.default,
			color: theme.palette.text.primary,
			borderBottomLeftRadius: theme.spacing(2),
			borderBottomRightRadius: theme.spacing(2),
			padding: theme.spacing(4)
		}
	},
	title: {
		alignSelf: "flex-start",
		textAlign: "left",
		textTransform: "uppercase",
		fontWeight: 500,
		fontSize: 16,
		letterSpacing: "0.38px"
	},
	items: {
		marginBottom: theme.spacing(2)
	},
	scroll: {
		height: "calc(100 * var(--vh, 1vh) - 255px)",
		position: "relative",
		alignItems: "center",
		overflowY: "scroll",
		paddingRight: theme.spacing(1)
	},
	scrollBox: {
		display: "flex",
		flexDirection: "column"
	},
	itemDivider: {
		marginTop: theme.spacing(2),
		backgroundColor: "#f2f2f2"
	},
	item: {
		marginLeft: theme.spacing(2),
		marginTop: theme.spacing(2),
		marginRight: theme.spacing(2),
		display: "flex",
		flexDirection: "row",
		alignItems: "center",
		justifyContent: "space-between"
	},
	itemTitle: {
		color: "#f2f2f2",
		textTransform: "uppercase",
		fontSize: "12px",
		letterSpacing: "0.38px"
	},
	itemTitleChecked: {
		color: "#fff"
	},
	radioIcon: {
		borderRadius: "50%",
		width: 13,
		height: 13,
		border: "1px solid #f2f2f2"
	},
	radioIconChecked: {
		borderRadius: "50%",
		width: 13,
		height: 13,
		border: "1px solid #fff",
		backgroundImage: "linear-gradient(180deg,hsla(0,0%,100%,.1),hsla(0,0%,100%,0))",
		"&:before": {
			display: "block",
			width: 11,
			height: 11,
			backgroundImage: "radial-gradient(#BDBDBD,#BDBDBD 56%,transparent 32%)",
			content: '""'
		}
	},
	titleRow: {
		marginTop: theme.spacing(1),
		marginBottom: theme.spacing(1),
		display: "flex",
		flexDirection: "row",
		alignItems: "center"
	},
	icon: {
		marginRight: 10,
		height: 18,
		width: 18,
		fill: "#fff",
		[theme.breakpoints.up(480)]: {
			fill: theme.palette.text.primary
		}
	},
	footer: {
		backgroundColor: "transparent",
		bottom: 0,
		left: 0,
		right: 0,
		width: "100%",
		marginRight: theme.spacing(1),
		padding: 0,
		position: "absolute",
		height: "150px",
		display: "flex",
		flexDirection: "column",
		justifyContent: "flex-end",
		[theme.breakpoints.up(480)]: {
			marginTop: 0,
			height: "auto",
			width: "auto",
			bottom: "6px",
			flex: "1",
			justifyContent: "center",
			padding: theme.spacing(2)
		}
	},
	terms: {
		borderTop: "1px solid #888",
		paddingTop: theme.spacing(1)
	},
	addButton: {
		backgroundColor: theme.palette.primary.main,
		color: theme.palette.primary.contrastText,
		borderRadius: 24,
		fontSize: 16,
		height: 48,
		width: "100%",
		padding: 0,
		alignContent: "center",
		justifyContent: "center",
		textTransform: "uppercase",
		"&:hover": {
			[theme.breakpoints.up(480)]: {
				backgroundColor: theme.palette.primary.main,
				color: theme.palette.primary.contrastText
			}
		}
	},
	buttonIcon: {
		position: "absolute",
		right: 10
	},
	actionButtonLabel: {
		marginLeft: theme.spacing(0.5)
	},
	textField: {
		marginTop: theme.spacing(1),
		marginBottom: theme.spacing(1),
		borderColor: "#fff",
		color: "#000",
		backgroundColor: "#f2f2f2",
		borderRadius: 8
	},
	test: {
		width: "100%"
	}
}));

interface Props {
	onChange: (orderTypeId: string) => void;
}

const CheckoutPanel = ({}: Props) => {
	const classes = useStyles();
	const { t } = useTranslation();
	const {
		grandTotalCents,
		fullName,
		phoneNumber,
		email,
		deliveryAddress,
		placeOrder,
		areAllItemsAvailable
	} = useCart();
	const [isCustomerInfoSubmitting] = useState(false);
	const [isCheckoutEnabled, setIsCheckoutEnabled] = useState(true);
	const [nameOnCard, setNameOnCard] = useState("");
	const [error, setError] = useState();
	const stripe = useStripe();
	const elements = useElements();
	const navigate = useNavigate();

	const [state, setState] = useState<{
		elementError: { [key in StripeElementType]?: string };
		elementValid: { [key in StripeElementType]?: boolean };
	}>({ elementError: {}, elementValid: {} });

	const allItemsAreAvailable = areAllItemsAvailable();

	const onDataChanged = (event: StripeElementChangeEvent) => {
		setState({
			...state,
			elementValid: {
				...state.elementValid,
				[event.elementType]: event.complete && !event.empty
			},
			elementError: {
				...state.elementError,
				[event.elementType]: event.error?.message
			}
		});
	};

	const isCardValid =
		nameOnCard && state.elementValid.cardNumber && state.elementValid.cardExpiry && state.elementValid.cardCvc;

	// const onCheckoutClicked = () => {
	// 	//navigate("#checkout");
	// };

	const onCustomerInfoUpdated = (isValid: boolean) => {};

	const submit = () => {
		if (!stripe || !elements) {
			// Stripe.js has not loaded yet. Make sure to disable
			// form submission until Stripe.js has loaded.
			console.error("Stripe.js not loaded");
			return;
		}
		setIsCheckoutEnabled(false);
		placeOrder({
			stripe,
			element: elements.getElement(CardNumberElement)!
		})
			.then(() => {
				navigate("#");
			})
			.catch(err => {
				setError(err.message);
				setIsCheckoutEnabled(true);
			});

		return false;
	};

	return (
		<>
			<Container className={classes.root}>
				<form onSubmit={submit}>
					<Box className={classes.body}>
						<Box className={classes.titleRow}>
							<ShoppingBagIcon height={20} width={20} className={classes.icon} />
							<Typography className={classes.title}>{t("placeOrder")}</Typography>
						</Box>

						<Box className={classes.scroll}>
							<Box className={classes.scrollBox}>
								<CustomerInfo
									fullName={fullName}
									phoneNumber={phoneNumber}
									email={email}
									deliveryAddress={deliveryAddress}
									isSubmitting={isCustomerInfoSubmitting}
									onSubmit={onCustomerInfoUpdated}
								/>

								<TextField
									className={classes.textField}
									fullWidth
									placeholder={t("nameOnCard").toUpperCase()}
									variant="outlined"
									inputProps={{ maxLength: 80 }}
									onChange={e => setNameOnCard(e.target.value)}
									value={nameOnCard}
									disabled={!isCheckoutEnabled}
									required
								/>

								<StripeTextField
									className={classes.textField}
									error={Boolean(state.elementError.cardNumber)}
									// helperText={state.elementError.cardNumber}
									inputProps={{
										options: {
											showIcon: true
										},
										id: "cardNumber"
									}}
									onChange={onDataChanged}
									stripeElement={CardNumberElement}
									disabled={!isCheckoutEnabled}
									required
								/>

								<StripeTextField
									className={classes.textField}
									error={Boolean(state.elementError.cardNumber)}
									// helperText={state.elementError.cardNumber}
									// inputProps={{
									// 	options: {
									// 		showIcon: true
									// 	}
									// }}
									onChange={onDataChanged}
									stripeElement={CardExpiryElement}
									disabled={!isCheckoutEnabled}
									required
								/>

								<StripeTextField
									className={classes.textField}
									error={Boolean(state.elementError.cardNumber)}
									// helperText={state.elementError.cardNumber}
									// inputProps={{
									// 	options: {
									// 		showIcon: true
									// 	}
									// }}
									onChange={onDataChanged}
									disabled={!isCheckoutEnabled}
									stripeElement={CardCvcElement}
								/>
								<Tip />
								<PromoCode />
								<OrderTotal />
							</Box>
						</Box>
					</Box>

					<Box className={classes.footer}>
						<Terms className={classes.terms} />
						<Button
							variant="outlined"
							className={classes.addButton}
							disabled={!isCheckoutEnabled || !isCardValid || !allItemsAreAvailable}
							onClick={submit}
						>
							{formatMoney(grandTotalCents)}
							<Typography className={classes.actionButtonLabel}>{` - ${t("placeOrder")}`}</Typography>
							<SignOutAltIcon className={classes.buttonIcon} />
						</Button>
					</Box>
				</form>
			</Container>

			<Dialog
				open={!!error}
				onClose={() => setError(undefined)}
				aria-labelledby="alert-dialog-title"
				aria-describedby="alert-dialog-description"
			>
				<DialogTitle id="alert-dialog-title">{t("Oppsss")}</DialogTitle>
				<DialogContent>
					<DialogContentText id="alert-dialog-description">{error}</DialogContentText>
				</DialogContent>
				<DialogActions>
					<Button onClick={() => setError(undefined)} color="primary">
						{t("Ok")}
					</Button>
				</DialogActions>
			</Dialog>
		</>
	);
};

export default CheckoutPanel;
